import { useEffect, useRef, useState } from "react";
import { MessageCircleHeart, Send, ThumbsUp, ThumbsDown, Trash2, ShieldAlert, Loader2, Mic } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Markdown } from "@/components/Markdown";
import { InterviewHistory } from "@/components/InterviewHistory";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API, errMsg } from "@/lib/api";

const PROMPTS = [
  "Which remote roles fit my experience?",
  "Turn this bedside bullet into a remote-ready one:",
  "Check this job posting for scam red flags:",
  "Run a mock utilization review interview with me",
  "Suggest keywords from this job posting:",
  "Review my resume for ATS-friendliness:",
];

function Message({ m, onRate }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`coach-message-${m.role}`}>
      <div className={`max-w-[88%] rounded-2xl px-4 py-3 text-sm ${mine ? "bg-teal-600 text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-700 rounded-bl-md"}`}>
        {mine ? <p className="whitespace-pre-wrap">{m.content}</p> : <Markdown className="coach-prose">{m.content || "…"}</Markdown>}
        {!mine && m.sources?.length > 0 && <p className="mt-2 text-[11px] text-slate-500">Sources: {m.sources.join(" · ")}</p>}
        {!mine && m.id && (
          <div className="mt-2 flex gap-1">
            <button onClick={() => onRate(m, 1)} aria-label="Helpful" className={`p-1 rounded hover:bg-slate-100 ${m.rating === 1 ? "text-teal-700" : "text-slate-400"}`} data-testid="coach-thumbs-up"><ThumbsUp className="h-3.5 w-3.5" /></button>
            <button onClick={() => onRate(m, -1)} aria-label="Not helpful" className={`p-1 rounded hover:bg-slate-100 ${m.rating === -1 ? "text-rose-600" : "text-slate-400"}`} data-testid="coach-thumbs-down"><ThumbsDown className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

const ROLES = ["Utilization review nurse", "Prior authorization nurse", "Nurse case manager", "Clinical documentation integrity (CDI) specialist", "Telephonic triage nurse", "Telehealth nurse", "Quality / HEDIS review nurse", "Clinical informatics specialist", "UK telephone triage / clinical assessment nurse", "UK virtual ward nurse"];

function MockSetup({ onStart, onCancel }) {
  const [role, setRole] = useState(ROLES[0]);
  const [bg, setBg] = useState("");
  return (
    <div className="space-y-2" data-testid="mock-interview-setup">
      <label className="text-xs font-semibold text-slate-700" htmlFor="mock-role">Target role</label>
      <select id="mock-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="mock-interview-role">
        {ROLES.map((r) => <option key={r}>{r}</option>)}
      </select>
      <input value={bg} onChange={(e) => setBg(e.target.value)} maxLength={300} placeholder="Optional: e.g. 5 years ICU, Epic super-user" className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm" aria-label="Your background" data-testid="mock-interview-background" />
      <p className="text-[11px] text-slate-500">5 questions, one at a time. Each answer is scored out of 10 on Situation, Task, Action, Result and role fit. Practice feedback only.</p>
      <div className="flex gap-2">
        <Button size="sm" className="rounded-full bg-violet-700 hover:bg-violet-800" onClick={() => onStart({ role, background: bg.trim(), id: crypto.randomUUID() })} data-testid="mock-interview-start"><Mic className="h-4 w-4 mr-1" />Start interview</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="mock-interview-cancel">Cancel</Button>
      </div>
    </div>
  );
}

export function CoachPanel({ moduleSlug, moduleLabel }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const [interview, setInterview] = useState(() => JSON.parse(localStorage.getItem("b2r_mock_interview") || "null"));
  const [setupOpen, setSetupOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const endRef = useRef(null);
  const saveInterview = (v) => { setInterview(v); v ? localStorage.setItem("b2r_mock_interview", JSON.stringify(v)) : localStorage.removeItem("b2r_mock_interview"); };

  useEffect(() => {
    if (!open) return;
    api.get("/coach/history").then((r) => { setMsgs(r.data.messages); setUsage(r.data); }).catch(() => {});
  }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (override, iv = interview) => {
    const text = (typeof override === "string" ? override : input).trim();
    if (!text || busy) return;
    if (typeof override !== "string") setInput("");
    setBusy(true);
    const k = Date.now();
    setMsgs((m) => [...m, { role: "user", content: text, k: `u${k}` }, { role: "assistant", content: "", k: `a${k}` }]);
    const extra = iv ? { mode: "mock_interview", interview_role: iv.role, interview_background: iv.background || null, interview_id: iv.id || null } : {};
    try {
      const res = await fetch(`${API}/coach/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: text, module_slug: moduleSlug || null, ...extra }) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || "Coach unavailable"); }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n"); buf = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ")) continue;
          const d = JSON.parse(ev.slice(6));
          setMsgs((m) => { const c = [...m]; const last = { ...c[c.length - 1] };
            if (d.delta) last.content += d.delta;
            if (d.done) { last.id = d.message_id; last.sources = d.sources; last.rating = 0; }
            c[c.length - 1] = last; return c; });
        }
      }
      setUsage((u) => u && { ...u, used_today: u.used_today + 1 });
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => m.slice(0, -1));
    } finally { setBusy(false); }
  };

  const rate = async (m, r) => {
    const rating = m.rating === r ? 0 : r;
    setMsgs((all) => all.map((x) => (x.id === m.id ? { ...x, rating } : x)));
    api.post("/coach/feedback", { message_id: m.id, rating }).catch(() => {});
  };
  const clear = async () => {
    if (!window.confirm("Clear your whole coach conversation history?")) return;
    try { await api.delete("/coach/history"); setMsgs([]); toast.success("Conversation cleared"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white pl-4 pr-5 py-3.5 shadow-xl shadow-teal-900/20 transition-colors" data-testid="coach-open-button">
        <MessageCircleHeart className="h-5 w-5" aria-hidden /> <span className="font-medium text-sm">Career Coach</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col h-[100dvh] overflow-hidden gap-0 bg-slate-50" data-testid="coach-panel">
          <SheetHeader className="px-5 pt-5 pb-3 bg-white border-b border-slate-200 text-left">
            <SheetTitle className="font-display text-xl">Remote Nursing Career Coach</SheetTitle>
            <SheetDescription className="text-xs">AI assistant · {moduleLabel ? `Context: ${moduleLabel}` : "Context: course dashboard"} · answers may contain mistakes</SheetDescription>
            <div className="mt-2 flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900" role="note" data-testid="coach-privacy-notice">
              <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
              <span>Never paste patient information or private personal identifiers (names, dates of birth, record numbers, SSNs). Not medical, legal or financial advice.</span>
            </div>
          </SheetHeader>
          {interview ? (
            <div className="px-4 py-2.5 bg-violet-50 border-b border-violet-200 flex items-center justify-between gap-2 text-xs text-violet-900" data-testid="mock-interview-banner">
              <span className="flex items-center gap-1.5"><Mic className="h-4 w-4" aria-hidden />Mock interview: <strong>{interview.role}</strong> · answer with STAR</span>
              <span className="flex gap-3">
                <button onClick={() => setHistoryOpen(true)} className="font-semibold underline underline-offset-2" data-testid="mock-interview-history-open">Scores</button>
                <button onClick={() => saveInterview(null)} className="font-semibold underline underline-offset-2" data-testid="mock-interview-end">End</button>
              </span>
            </div>
          ) : (
            <div className="px-4 py-2.5 bg-white border-b border-slate-200">
              {!setupOpen ? (
                <Button size="sm" variant="outline" className="rounded-full w-full border-violet-300 text-violet-900 hover:bg-violet-50" onClick={() => setSetupOpen(true)} data-testid="mock-interview-open"><Mic className="h-4 w-4 mr-1.5" />Start a mock interview (scored with STAR)</Button>
              ) : null}
              {!setupOpen ? (
                <button onClick={() => setHistoryOpen(true)} className="mt-1.5 w-full text-xs text-violet-800 underline underline-offset-2" data-testid="mock-interview-history-open">View my score history</button>
              ) : (
                <MockSetup onCancel={() => setSetupOpen(false)} onStart={(iv) => { saveInterview(iv); setSetupOpen(false); send(`Start my mock interview for a ${iv.role} role.${iv.background ? ` My background: ${iv.background}` : ""}`, iv); }} />
              )}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3" data-testid="coach-messages">
            {msgs.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Hi, I'm your AI career coach. I use the course content to help you. Try one of these:</p>
                <div className="flex flex-wrap gap-2">
                  {PROMPTS.map((p, i) => <button key={p} onClick={() => setInput(p + (p.endsWith(":") ? "\n" : ""))} className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-teal-400 hover:text-teal-800 transition-colors text-left" data-testid={`coach-prompt-${i}`}>{p}</button>)}
                </div>
              </div>
            )}
            {msgs.map((m, i) => <Message key={m.k || m.id || `i${i}`} m={m} onRate={rate} />)}
            {busy && msgs[msgs.length - 1]?.content === "" && <Loader2 className="h-4 w-4 animate-spin text-teal-700" />}
            <div ref={endRef} />
          </div>
          <div className="bg-white border-t border-slate-200 p-3 space-y-2">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={interview ? "Type your answer (Situation, Task, Action, Result)… or 'skip'" : "Ask about roles, resumes, interviews, scams…"} rows={3} className="resize-none" aria-label="Message the coach" data-testid="coach-input" />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <button onClick={clear} className="flex items-center gap-1 hover:text-rose-600" data-testid="coach-clear-history"><Trash2 className="h-3.5 w-3.5" /> Clear history</button>
                {usage && <span data-testid="coach-usage">{usage.used_today}/{usage.daily_limit} today</span>}
              </div>
              <Button onClick={() => send()} disabled={busy || !input.trim()} className="rounded-full" data-testid="coach-send-button"><Send className="h-4 w-4 mr-1.5" /> Send</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="interview-history-dialog">
          <DialogHeader><DialogTitle>Mock interview score history</DialogTitle></DialogHeader>
          <InterviewHistory refreshKey={msgs.length} />
        </DialogContent>
      </Dialog>
    </>
  );
}
